import { useLayoutEffect, useRef, useState } from "react";

// Height (px) reserved for the antd Table's own sticky header + pagination row.
// Those render OUTSIDE the scrollable body, so the body (scroll.y) gets whatever
// vertical space is left after subtracting them from the wrapper's height. Sized
// with headroom above the real chrome (~single-row header ~55 + pagination ~48)
// so the pagination is never clipped by the wrapper's overflow:hidden.
const TABLE_CHROME = 140;

// Smallest wrapper height we'll ever set. Must be at least TABLE_CHROME + the
// bodyHeight floor (below) so the header + a minimal body + the pagination row
// always fit inside the wrapper — otherwise, since the wrapper is overflow:hidden,
// the pagination would be clipped with no scroll affordance to reach it. Above
// this floor there is always MORE body room, so the chrome never clips. On a
// viewport too short to give even this much space, the page (not the table)
// scrolls — unavoidable, and pagination stays reachable via that page scroll.
const MIN_BODY_HEIGHT = 80;
const MIN_TABLE_HEIGHT = TABLE_CHROME + MIN_BODY_HEIGHT; // 220

// Walks up from `el` to the nearest ancestor that actually scrolls vertically.
// That element — not the window — is the real boundary we must fit inside, and
// it may add its own padding / row-gap below us.
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// Sizes the table's wrapper and its antd `scroll.y` (body height) so the whole
// table — header, body, and pagination — fits exactly inside the scroll
// container's content box. The wrapper is given a fixed height with
// overflow:hidden, so the PAGE never scrolls: a tall/expanded row scrolls WITHIN
// the table body instead of growing the container. Recomputes on resize.
//
// Returns `height` for the wrapper div (set overflow:hidden on it) and
// `bodyHeight` for the Table's `scroll.y`.
export function useTableScrollHeight() {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(() =>
    Math.max(MIN_TABLE_HEIGHT, window.innerHeight - 400)
  );

  useLayoutEffect(() => {
    const compute = () => {
      const el = ref.current;
      if (!el) return;

      const top = el.getBoundingClientRect().top;

      // Fit inside the real scroll container's content box: subtract its
      // bottom padding and any row-gap it reserves below us (an EVA settings
      // grid, e.g. `.organizationAccount`, does both). Fall back to the
      // viewport bottom when there is no scrolling ancestor.
      let bottomLimit = window.innerHeight;
      const parent = getScrollParent(el);
      if (parent) {
        const style = getComputedStyle(parent);
        const paddingBottom = parseFloat(style.paddingBottom) || 0;
        const rowGap = parseFloat(style.rowGap) || 0;
        bottomLimit = parent.getBoundingClientRect().bottom - paddingBottom - rowGap;
      }

      setHeight(Math.max(MIN_TABLE_HEIGHT, Math.floor(bottomLimit - top)));
    };

    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const bodyHeight = Math.max(MIN_BODY_HEIGHT, height - TABLE_CHROME);
  return { ref, height, bodyHeight };
}
